<template>
    <div class="fees-by-product w-100 display-flex flex-column">
        <!-- Original product-based chart -->
        <AonCol class="grid-cols-12 px-0">
            <AonCard title="Upcoming Fees - By Product" :desc="computedDescription">
                <template #header-append>
                    <div class="display-flex align-items-center">
                        <p class="bold ml-lg" style="min-width: 64px">Sort by:</p>
                        <ArmoryOptionToggle
                            class="ml-sm"
                            style="min-width: 326px"
                            v-model="toggleValue"
                            :options="[
                                { label: 'Fees', value: 'fee' },
                                { label: 'Momentum', value: 'momentum' },
                                { label: 'Market Appeal', value: 'marketAppeal' },
                            ]"
                            @update:modelValue="handleToggleChange"
                        />
                    </div>
                </template>
                <div class="inner-content">
                    <div id="feesBarChart"></div>
                </div>
            </AonCard>
            <AonCoverLoading
                :loading="props.data.length === 0 && !props.loading"
                :show-spinner="false"
                :title="`No Fees By Product`"
                :message="`No fees were found for ${entityStore.entity?.name} between ${portfolioManagerStore.dateRange[0]} and ${portfolioManagerStore.dateRange[1]}`"
            />
        </AonCol>
    </div>
</template>

<script setup>
import { watch, computed, nextTick, onUnmounted, ref } from 'vue'
import { useEntityStore, usePortfolioManagerStore } from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useBarChart } from '@/composables/charts/barChart'
import { config } from '@/config'

import moment from 'moment'

const entityStore = useEntityStore()
const portfolioManagerStore = usePortfolioManagerStore()

let rootProduct
let barChartProduct

const emit = defineEmits(['item-clicked'])
const props = defineProps({
    data: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    showProductLabels: {
        type: Boolean,
        default: true,
    },
})

const toggleValue = ref('fee')

watch(
    () => props.data,
    () => {
        initializeCharts()
    }
)

onUnmounted(() => {
    if (rootProduct) rootProduct.dispose()
})

const computedDescription = computed(() => {
    return toggleValue.value === 'fee'
        ? 'Sorts products in descending order by maintenance fees over the selected timeframe, placing the product with the highest fees at the top. This view helps identify areas of significant investment that may warrant closer scrutiny.'
        : toggleValue.value === 'momentum'
          ? 'Sorts products in ascending order by momentum, placing the product with the lowest momentum at the top. Low-momentum areas may indicate stagnation and could be ripe for pruning.'
          : 'Sorts products in ascending order by predicted market appeal, which is an estimate of how a product might be valued or recognized based on valuations of comparable publicly traded companies operating in similar innovation domains. This view highlights products with relatively low market recognition, which may benefit from pruning.'
})

const productData = computed(() => {
    // First sort the products based on the selected metric
    const sortedProducts = [...props.data].sort((a, b) => {
        if (toggleValue.value === 'momentum') {
            return b.momentum - a.momentum
        } else if (toggleValue.value === 'marketAppeal') {
            return b.marketAppeal - a.marketAppeal
        }
        return a.upcomingFeeAmount - b.upcomingFeeAmount
    })

    // Convert to the format expected by the bar chart
    return sortedProducts.map((entry) => {
        return {
            name: entry.product,
            value: entry.upcomingFeeAmount,
            dateRange: {
                from: portfolioManagerStore.dateRange[0],
                to: portfolioManagerStore.dateRange[1],
            },
        }
    })
})

const initializeCharts = async () => {
    // Initialize product chart
    if (rootProduct) {
        rootProduct.dispose()
    }

    await nextTick()

    am5.addLicense(config.license.AMChartsLicense)

    // Create product chart
    if (productData.value.length > 0) {
        rootProduct = am5.Root.new('feesBarChart')
        barChartProduct = useBarChart(rootProduct)

        barChartProduct.setClickCallback(handleBarClick)

        barChartProduct.configureBar({ horizontal: true, scrollable: true })
        barChartProduct.generateAxes(productData.value, {
            showBullets: props.showProductLabels,
            horizontal: true,
            formatCurrency: true,
            scrollable: true,
        })
        barChartProduct.generateSeries(productData.value, {
            horizontal: true,
            formatCurrency: true,
            clickable: true,
        })
    }
}

const handleBarClick = (barData) => {
    if (barData.date) {
        delete barData.name
        delete barData.originalData.name
    }
    emit('item-clicked', barData)
}

const handleToggleChange = (value) => {
    toggleValue.value = value
    initializeCharts()
}
</script>

<style lang="scss" scoped>
.fees-by-product {
    width: 100%;
    position: relative;

    .card-content {
        width: 100%;
    }

    #feesMonthlyBarChart {
        width: 100%;
        min-height: 200px;

        div {
            height: 100%;
        }
    }

    #feesBarChart {
        width: 100%;
        min-height: 400px;

        div {
            height: 100%;
        }
    }
}
</style>
